import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';


/**
 * ResizableTable
 *
 * A table component with resizable columns, scrollable body, and optional expandable rows.
 *
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions { key, label, minWidth, maxWidth, width }
 * @param {Array} props.data - Array of row data objects
 * @param {number|string} props.height - CSS height for the scrollable table body
 * @param {function} props.renderCell - Function (row, colKey) => ReactNode
 * @param {function} [props.getRowClass] - Optional. Function (row) => string. Adds className to each row.
 * @param {function} [props.expandableRowRender] - Optional. Function (row, rowIdx, expanded) => ReactNode. If provided, supports expandable rows.
 * @param {number|number[]} [props.expandedRowIdx] - Optional. Controlled expanded row(s) index (single or array for multi-expand). If not provided, expansion is managed internally.
 * @param {function} [props.onExpand] - Optional. Callback (rowIdx, expanded) when a row is expanded/collapsed. Use for controlled expansion.
 */
const ResizableTable = ({ columns, data, height: initialHeight, renderCell, getRowClass, expandableRowRender, expandedRowKey: controlledExpandedRowKey, onExpand, getRowKey }) => {
  // Use getRowKey to uniquely identify each row (default: rowIdx)
  const keyForRow = typeof getRowKey === 'function' ? getRowKey : (_row, idx) => idx;

  // Internal expansion state if not controlled
  const [internalExpandedRowKey, setInternalExpandedRowKey] = useState(null);
  const expandedRowKey = controlledExpandedRowKey !== undefined ? controlledExpandedRowKey : internalExpandedRowKey;

  // Handles row expand/collapse
  const handleExpandRow = (row, rowIdx) => {
    const rowKey = keyForRow(row, rowIdx);
    if (onExpand) {
      const isExpanded = expandedRowKey === rowKey;
      onExpand(rowKey, !isExpanded);
    } else {
      setInternalExpandedRowKey(cur => (cur === rowKey ? null : rowKey));
    }
  };

  const [tableHeight, setTableHeight] = useState(initialHeight || 350);
  const resizing = useRef(false);
  const startY = useRef(null);
  const startHeight = useRef(null);



  // Mouse events for resizing
  const onResizeMouseDown = (e) => {
    resizing.current = true;
    startY.current = e.clientY;
    startHeight.current = tableHeight;
    document.addEventListener('mousemove', onResizeMouseMove);
    document.addEventListener('mouseup', onResizeMouseUp);
    e.preventDefault();
  };

  const onResizeMouseMove = (e) => {
    if (resizing.current) {
      const delta = e.clientY - startY.current;
      let newHeight = Math.max(120, startHeight.current + delta);
      setTableHeight(newHeight);
    }
  };

  const onResizeMouseUp = () => {
    resizing.current = false;
    document.removeEventListener('mousemove', onResizeMouseMove);
    document.removeEventListener('mouseup', onResizeMouseUp);
  };
  const [colWidths, setColWidths] = useState(() =>
    columns.map(col => col.width || 150)
  );
  const resizingCol = useRef(null);
  const startX = useRef(null);
  const startWidth = useRef(null);

  const onMouseDown = (e, colIdx) => {
    resizingCol.current = colIdx;
    startX.current = e.clientX;
    startWidth.current = colWidths[colIdx];
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = e => {
    if (resizingCol.current !== null) {
      const delta = e.clientX - startX.current;
      setColWidths(widths => {
        const newWidths = [...widths];
        let newWidth = startWidth.current + delta;
        const min = columns[resizingCol.current].minWidth || 60;
        const max = columns[resizingCol.current].maxWidth || 600;
        newWidth = Math.max(min, Math.min(max, newWidth));
        newWidths[resizingCol.current] = newWidth;
        return newWidths;
      });
    }
  };

  const onMouseUp = () => {
    resizingCol.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };


  return (
    <div style={{ width: '100%', height: tableHeight, position: 'relative' }} className="resizable-table-wrapper">
      {/* Scrollable table container */}
      <div 
        className="resizable-table-container" 
        style={{ 
          width: '100%', 
          height: '100%', 
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <table className="log-events-table resizable-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            {columns.map((col, i) => (
              <col key={col.key} style={{ width: colWidths[i] }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th
                  key={col.key}
                  style={{ position: 'relative', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{col.label}</span>
                    <span
                      className="resize-handle"
                      style={{ cursor: 'col-resize', padding: '0 4px', userSelect: 'none' }}
                      onMouseDown={e => onMouseDown(e, i)}
                    >
                      ||
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/*
              Render all rows normally. If there are no rows, show a single empty state row.
              This ensures the table structure (headers, etc.) is always visible, even when filtered data is empty.
            */}
            {data.length > 0 ? (
              data.map((row, rowIdx) => {
                const rowKey = keyForRow(row, rowIdx);
                const expanded = expandedRowKey === rowKey;
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={typeof getRowClass === 'function' ? getRowClass(row) : ''}
                      onDoubleClick={expandableRowRender ? () => handleExpandRow(row, rowIdx) : undefined}
                      tabIndex={expandableRowRender ? 0 : undefined}
                      title={expandableRowRender ? (expanded ? 'Double-click to collapse' : 'Double-click to expand') : undefined}
                      style={expandableRowRender ? {cursor: 'pointer'} : undefined}
                    >
                      {columns.map((col, colIdx) => (
                        <td key={col.key} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {renderCell(row, col.key)}
                        </td>
                      ))}
                    </tr>
                    {expandableRowRender && expanded && (
                      <tr className="resizable-table-expanded-row">
                        <td colSpan={columns.length} style={{padding:0, border:'none'}}>
                          {expandableRowRender(row, rowIdx, true)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr className="log-events-empty-row">
                <td colSpan={columns.length} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: '1.2em 0' }}>
                  No log events available for this file.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Resize handle - positioned outside the scroll context */}
      <div
        className="table-vertical-resize-handle"
        style={{
          position: 'absolute',
          right: 16,
          bottom: 4,
          width: 18,
          height: 18,
          cursor: 'ns-resize',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.1)',
          boxShadow: '0 0 4px rgba(0,0,0,0.2)',
          borderRadius: '3px',
          userSelect: 'none',
        }}
        onMouseDown={onResizeMouseDown}
        title="Drag to resize table height"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" style={{opacity: 0.7}}>
          <path d="M1 10h10M3 7h6M5 4h2" stroke="#999" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );
};

ResizableTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
      width: PropTypes.number
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  renderCell: PropTypes.func.isRequired,
  getRowKey: PropTypes.func,
  expandedRowKey: PropTypes.any,
  onExpand: PropTypes.func,
  expandableRowRender: PropTypes.func,
  getRowClass: PropTypes.func,
};

ResizableTable.defaultProps = {
  height: 350,
  getRowKey: (_row, idx) => idx,
};

export default ResizableTable;
